import React from 'react';
import { useLanguage } from '../context/LanguageContext';
import LanguageSelector from './LanguageSelector';
import '../styles/scandinavian-theme.css';

interface LandingScandinavianProps {
  onBookNow: () => void;
}

const LandingScandinavian: React.FC<LandingScandinavianProps> = ({ onBookNow }) => {
  const { t } = useLanguage();

  return (
    <div className="min-h-screen" style={{ backgroundColor: '#FDFBF9' }}>
      {/* ========== 1. HEADER / HERO SECTION ========== */}
      <section style={{ 
        position: 'relative',
        minHeight: '90vh',
        overflow: 'hidden'
      }}>
        {/* Header - Absolute positioned */}
        <header style={{ 
          padding: '2rem 3rem',
          position: 'absolute',
          top: 0,
          left: 0,
          right: 0,
          zIndex: 10
        }}>
          <div style={{ 
            display: 'flex', 
            justifyContent: 'space-between', 
            alignItems: 'center',
            maxWidth: '1400px',
            margin: '0 auto'
          }}>
            <span style={{ 
              fontFamily: 'Cormorant, Georgia, serif',
              fontSize: '1.5rem',
              fontWeight: 400,
              color: '#6B5B4F',
              letterSpacing: '0.02em'
            }}>
              SheCare
            </span>
            <LanguageSelector />
          </div>
        </header>

        {/* Full-width background image */}
        <div style={{
          position: 'absolute',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          zIndex: 0
        }}>
          <img 
            src="/images/hero-background.png"
            alt="Calm wellness scene"
            style={{
              width: '100%',
              height: '100%',
              objectFit: 'cover',
              objectPosition: 'center 15%'
            }}
          />
          {/* Bottom-only gradient for text readability */}
          <div style={{
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            background: 'linear-gradient(to bottom, rgba(253, 251, 249, 0) 0%, rgba(253, 251, 249, 0) 50%, rgba(253, 251, 249, 0.7) 100%)'
          }}></div>
        </div>

        {/* Hero content - anchored to bottom of section */}
        <div style={{
          position: 'absolute',
          left: '50%',
          bottom: '2rem',
          transform: 'translateX(-50%)',
          zIndex: 1,
          textAlign: 'center',
          maxWidth: '1000px',
          padding: '0 1.5rem',
          width: '100%'
        }}>
          {/* Headline */}
          <h1 style={{
            fontFamily: 'Cormorant, Georgia, serif',
            fontSize: 'clamp(1.75rem, 5vw, 4rem)',
            fontWeight: 400,
            color: '#6B5B4F',
            lineHeight: 1.2,
            marginBottom: '0.5rem',
            letterSpacing: '0.01em'
          }}>
            Care for Women, <span style={{ color: '#8B6F5F' }}>Delivered Gently</span>
          </h1>
          
          {/* Subtext */}
          <p style={{
            fontSize: 'clamp(0.9375rem, 2vw, 1.125rem)',
            color: '#8B7E74',
            marginBottom: '1rem',
            lineHeight: 1.5,
            fontWeight: 300,
            maxWidth: '100%',
            margin: '0 auto 1rem'
          }}>
            Private consultations with trusted doctors, wherever you are.
          </p>
          
          {/* CTA Button */}
          <button 
            onClick={onBookNow}
            style={{
              padding: '0.875rem 2rem',
              backgroundColor: '#C9BDB0',
              color: '#FFFFFF',
              border: 'none',
              borderRadius: '24px',
              fontSize: 'clamp(0.875rem, 2vw, 0.9375rem)',
              fontWeight: 400,
              letterSpacing: '0.02em',
              cursor: 'pointer',
              transition: 'background-color 0.3s ease',
              marginBottom: '1rem',
              display: 'inline-flex',
              alignItems: 'center',
              gap: '0.5rem'
            }}
            onMouseEnter={(e) => e.currentTarget.style.backgroundColor = '#B5A599'}
            onMouseLeave={(e) => e.currentTarget.style.backgroundColor = '#C9BDB0'}
          >
            Book a Consultation
            <span>→</span>
          </button>
          
          {/* Trust indicators */}
          <div style={{
            display: 'flex',
            justifyContent: 'center',
            gap: '1rem',
            fontSize: 'clamp(0.75rem, 1.5vw, 0.875rem)',
            color: '#9B9490',
            flexWrap: 'wrap'
          }}>
            <span>Licensed Doctors</span>
            <span>•</span>
            <span>Hosted in Scandinavia</span>
            <span>•</span>
            <span>Privacy-First</span>
          </div>
        </div>
      </section>

      {/* ========== 2. THIS IS FOR YOU IF... SECTION ========== */}
      <section style={{ 
        backgroundColor: '#FDFBF9',
        overflow: 'hidden'
      }}>
        <div style={{ 
          display: 'flex',
          flexDirection: 'row',
          alignItems: 'center',
          width: '100%'
        }}>
          {/* Left Column - Text Content (40%) */}
          <div style={{
            width: '40%',
            padding: '100px 60px',
            zIndex: 2,
            position: 'relative'
          }}>
            <h2 style={{
              fontFamily: 'Cormorant, Georgia, serif',
              fontSize: 'clamp(2rem, 3.5vw, 2.75rem)',
              fontWeight: 300,
              color: '#6B5B4F',
              marginBottom: '3rem',
              textAlign: 'center',
              letterSpacing: '-0.01em'
            }}>
              This is for you if...
            </h2>

            <div style={{ display: 'flex', flexDirection: 'column', gap: '2rem' }}>
              {[
                'You live abroad and want care in your own language',
                'You want privacy and security, without judgment',
                'You care for your parents from afar'
              ].map((item, index) => (
                <div key={index} style={{ 
                  display: 'flex', 
                  gap: '1rem', 
                  alignItems: 'flex-start' 
                }}>
                  <svg 
                    width="24" 
                    height="24" 
                    viewBox="0 0 24 24" 
                    fill="none" 
                    style={{ flexShrink: 0, marginTop: '2px' }}
                  >
                    <circle 
                      cx="12" 
                      cy="12" 
                      r="10" 
                      stroke="#B8C7B3" 
                      strokeWidth="1.5"
                    />
                    <path 
                      d="M8 12.5L10.5 15L16 9.5" 
                      stroke="#B8C7B3" 
                      strokeWidth="1.5" 
                      strokeLinecap="round" 
                      strokeLinejoin="round"
                    />
                  </svg>
                  
                  <p style={{
                    fontSize: '1.0625rem',
                    color: '#6B6560',
                    lineHeight: 1.7,
                    margin: 0,
                    fontFamily: 'Inter, sans-serif',
                    fontWeight: 300
                  }}>
                    {item}
                  </p>
                </div>
              ))}
            </div>
          </div>

          {/* Right Column - Image with Gradient Mask (60%) */}
          <div style={{
            width: '60%',
            position: 'relative',
            alignSelf: 'stretch'
          }}>
            <img 
              src="/images/section-for-you.png"
              alt="Serene wellness scene with tea, dried flowers and natural materials"
              style={{
                width: '100%',
                height: '100%',
                minHeight: '600px',
                objectFit: 'cover',
                objectPosition: 'center',
                display: 'block'
              }}
              loading="lazy"
            />
            
            <div style={{
              position: 'absolute',
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              background: 'linear-gradient(to right, #FDFBF9 0%, rgba(253, 251, 249, 0.8) 15%, rgba(253, 251, 249, 0) 35%)',
              zIndex: 1,
              pointerEvents: 'none'
            }}></div>
          </div>
        </div>
      </section>

      {/* ========== 3. HOW SHECARE WORKS SECTION ========== */}
      <section style={{ 
        padding: '100px 60px',
        backgroundColor: '#FAF8F5',
        textAlign: 'center'
      }}>
        <h2 style={{
          fontFamily: 'Cormorant, Georgia, serif',
          fontSize: 'clamp(2rem, 3.5vw, 2.75rem)',
          fontWeight: 300,
          color: '#6B5B4F',
          marginBottom: '5rem',
          letterSpacing: '-0.01em'
        }}>
          How SheCare Works
        </h2>

        <div style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(3, 1fr)',
          gap: '4rem',
          maxWidth: '1200px',
          margin: '0 auto'
        }}>
          {/* Step 1 */}
          <div>
            <div style={{
              fontFamily: 'Cormorant, Georgia, serif',
              fontSize: '3rem',
              fontWeight: 300,
              color: '#C9BDB0',
              marginBottom: '1.5rem'
            }}>
              1.
            </div>
            <h3 style={{
              fontFamily: 'Cormorant, Georgia, serif',
              fontSize: '1.5rem',
              fontWeight: 400,
              color: '#6B5B4F',
              marginBottom: '1rem'
            }}>
              Choose a doctor
            </h3>
            <p style={{
              fontSize: '0.9375rem',
              color: '#8B7E74',
              lineHeight: 1.7,
              fontWeight: 300
            }}>
              Find a doctor who offers consultations on what matters to you.
            </p>
          </div>

          {/* Step 2 */}
          <div>
            <div style={{
              fontFamily: 'Cormorant, Georgia, serif',
              fontSize: '3rem',
              fontWeight: 300,
              color: '#C9BDB0',
              marginBottom: '1.5rem'
            }}>
              2.
            </div>
            <h3 style={{
              fontFamily: 'Cormorant, Georgia, serif',
              fontSize: '1.5rem',
              fontWeight: 400,
              color: '#6B5B4F',
              marginBottom: '1rem'
            }}>
              Choose a time
            </h3>
            <p style={{
              fontSize: '0.9375rem',
              color: '#8B7E74',
              lineHeight: 1.7,
              fontWeight: 300
            }}>
              Determine a time that is convenient, without timezone headaches.
            </p>
          </div>

          {/* Step 3 */}
          <div>
            <div style={{
              fontFamily: 'Cormorant, Georgia, serif',
              fontSize: '3rem',
              fontWeight: 300,
              color: '#C9BDB0',
              marginBottom: '1.5rem'
            }}>
              3.
            </div>
            <h3 style={{
              fontFamily: 'Cormorant, Georgia, serif',
              fontSize: '1.5rem',
              fontWeight: 400,
              color: '#6B5B4F',
              marginBottom: '1rem'
            }}>
              Talk in private
            </h3>
            <p style={{
              fontSize: '0.9375rem',
              color: '#8B7E74',
              lineHeight: 1.7,
              fontWeight: 300
            }}>
              When booking, you enjoy a consultation visit that's safe, and strictly not judgmental.
            </p>
          </div>
        </div>
      </section>

      {/* ========== 4. CARE DOESN'T STOP AT BORDERS SECTION ========== */}
      <section style={{ 
        backgroundColor: '#FAF8F5',
        overflow: 'hidden'
      }}>
        <div style={{ 
          display: 'flex',
          flexDirection: 'row',
          alignItems: 'center',
          width: '100%'
        }}>
          {/* Left Column - Image with Gradient Mask (60%) */}
          <div style={{
            width: '60%',
            position: 'relative',
            alignSelf: 'stretch'
          }}>
            <img 
              src="/images/parents.png"
              alt="Mother and daughter hands holding gently"
              style={{
                width: '100%',
                height: '100%',
                minHeight: '600px',
                objectFit: 'cover',
                objectPosition: 'center',
                display: 'block'
              }}
              loading="lazy"
            />
            
            <div style={{
              position: 'absolute',
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              background: 'linear-gradient(to left, #FAF8F5 0%, rgba(250, 248, 245, 0.8) 15%, rgba(250, 248, 245, 0) 35%)',
              zIndex: 1,
              pointerEvents: 'none'
            }}></div>
          </div>

          {/* Right Column - Text Content (40%) */}
          <div style={{
            width: '40%',
            padding: '100px 60px',
            zIndex: 2,
            position: 'relative'
          }}>
            {/* Headline - Intentional line breaks */}
            <h2 style={{
              fontFamily: 'Cormorant, Georgia, serif',
              fontSize: 'clamp(2rem, 3.5vw, 2.75rem)',
              fontWeight: 300,
              color: '#6B5B4F',
              marginBottom: '2rem',
              letterSpacing: '-0.01em',
              lineHeight: 1.3
            }}>
              Care doesn't stop at borders.<br />
              Neither should<br />
              your parents' health.
            </h2>

            {/* Body Text - Short, calm */}
            <p style={{
              fontSize: '1.0625rem',
              color: '#6B6560',
              lineHeight: 1.7,
              marginBottom: '2.5rem',
              fontWeight: 300
            }}>
              Help your parents get the care they need, no matter where you are.
            </p>

            {/* CTA Button */}
            <button 
              onClick={onBookNow}
              style={{
                padding: '1rem 2.5rem',
                backgroundColor: '#C9BDB0',
                color: '#FFFFFF',
                border: 'none',
                borderRadius: '24px',
                fontSize: '0.9375rem',
                fontWeight: 400,
                letterSpacing: '0.02em',
                cursor: 'pointer',
                transition: 'background-color 0.3s ease'
              }}
              onMouseEnter={(e) => e.currentTarget.style.backgroundColor = '#B5A599'}
              onMouseLeave={(e) => e.currentTarget.style.backgroundColor = '#C9BDB0'}
            >
              Book a Consultation
            </button>
          </div>
        </div>
      </section>

      {/* Footer - Minimal */}
      <footer style={{ 
        borderTop: '1px solid #EDE7DD',
        padding: '2rem 3rem',
        backgroundColor: '#FDFBF9'
      }}>
        <div style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          maxWidth: '1400px',
          margin: '0 auto',
          flexWrap: 'wrap',
          gap: '1.5rem'
        }}>
          <div style={{ fontSize: '0.875rem', color: '#9B9490' }}>
            © 2026 SheCare
          </div>
          <div style={{ display: 'flex', gap: '2rem', fontSize: '0.875rem' }}>
            <a href="/faq" style={{ color: '#9B9490', textDecoration: 'none' }}>FAQ</a>
            <a href="/terms" style={{ color: '#9B9490', textDecoration: 'none' }}>Terms</a>
            <span style={{ color: '#9B9490', cursor: 'pointer' }}>Privacy</span>
          </div>
        </div>
      </footer>

      {/* Mobile Responsive Styles */}
      <style>{`
        /* Tablet and below */
        @media (max-width: 768px) {
          /* ===== HERO SECTION - PRESERVE COMPOSITED LOOK ===== */
          /* Keep minimum height so negative space exists */
          section:first-of-type {
            min-height: 85vh !important;
          }
          
          /* Hero header - reduce padding */
          header {
            padding: 1.5rem 1.5rem !important;
          }
          
          /* CRITICAL: Keep background image positioned to show objects at top */
          section:first-of-type > div:first-child img {
            object-position: center 10% !important;
          }
          
          /* CRITICAL: Preserve the bottom gradient overlay on mobile */
          section:first-of-type > div:first-child > div {
            background: linear-gradient(to bottom, rgba(253, 251, 249, 0) 0%, rgba(253, 251, 249, 0) 40%, rgba(253, 251, 249, 0.75) 100%) !important;
          }
          
          /* Hero content - adjust positioning to sit in empty linen space */
          section:first-of-type > div:last-child {
            bottom: 2.5rem !important;
            padding: 0 1.5rem !important;
          }
          
          /* Trust indicators - ensure they wrap nicely */
          section:first-of-type > div:last-child > div:last-child {
            gap: 0.75rem !important;
            font-size: 0.75rem !important;
          }
          
          /* ===== SECTION 2 - PRESERVE BLENDED AESTHETIC ===== */
          /* Keep as overlay composition, not stacked */
          section:nth-of-type(2) > div {
            position: relative !important;
            min-height: 85vh !important;
          }
          
          /* Image becomes full background on mobile */
          section:nth-of-type(2) > div > div:last-child {
            position: absolute !important;
            top: 0 !important;
            left: 0 !important;
            right: 0 !important;
            bottom: 0 !important;
            width: 100% !important;
            z-index: 0 !important;
          }
          
          section:nth-of-type(2) > div > div:last-child img {
            width: 100% !important;
            height: 100% !important;
            object-fit: cover !important;
            object-position: center !important;
          }
          
          /* CRITICAL: Keep gradient overlay - fade from bottom up */
          section:nth-of-type(2) > div > div:last-child > div {
            background: linear-gradient(to bottom, rgba(253, 251, 249, 0) 0%, rgba(253, 251, 249, 0) 30%, rgba(253, 251, 249, 0.85) 70%, rgba(253, 251, 249, 0.95) 100%) !important;
          }
          
          /* Text overlays on image with proper positioning */
          section:nth-of-type(2) > div > div:first-child {
            position: relative !important;
            z-index: 2 !important;
            width: 100% !important;
            padding: 60px 24px !important;
            margin-top: 50vh !important;
            background: transparent !important;
          }
          
          /* ===== SECTION 3 - HOW SHECARE WORKS ===== */
          section:nth-of-type(3) {
            padding: 60px 24px !important;
          }
          
          section:nth-of-type(3) > div {
            grid-template-columns: 1fr !important;
            gap: 3rem !important;
          }
          
          /* ===== SECTION 4 - PRESERVE BLENDED AESTHETIC ===== */
          /* Keep as overlay composition, not stacked */
          section:nth-of-type(4) > div {
            position: relative !important;
            min-height: 85vh !important;
          }
          
          /* Image becomes full background on mobile */
          section:nth-of-type(4) > div > div:first-child {
            position: absolute !important;
            top: 0 !important;
            left: 0 !important;
            right: 0 !important;
            bottom: 0 !important;
            width: 100% !important;
            z-index: 0 !important;
          }
          
          section:nth-of-type(4) > div > div:first-child img {
            width: 100% !important;
            height: 100% !important;
            object-fit: cover !important;
            object-position: center !important;
          }
          
          /* CRITICAL: Keep gradient overlay - fade from bottom up */
          section:nth-of-type(4) > div > div:first-child > div {
            background: linear-gradient(to bottom, rgba(250, 248, 245, 0) 0%, rgba(250, 248, 245, 0) 30%, rgba(250, 248, 245, 0.85) 70%, rgba(250, 248, 245, 0.95) 100%) !important;
          }
          
          /* Text overlays on image with proper positioning */
          section:nth-of-type(4) > div > div:last-child {
            position: relative !important;
            z-index: 2 !important;
            width: 100% !important;
            padding: 60px 24px !important;
            margin-top: 50vh !important;
            background: transparent !important;
          }
          
          /* Footer - stack on mobile */
          footer > div {
            flex-direction: column !important;
            text-align: center !important;
            gap: 1rem !important;
          }
        }
        
        /* iPhone specific (375px - 414px) */
        @media (max-width: 414px) {
          /* ===== HERO - FINE-TUNE FOR SMALL SCREENS ===== */
          section:first-of-type {
            min-height: 75vh !important;
          }
          
          /* Adjust text positioning lower to stay in empty space */
          section:first-of-type > div:last-child {
            bottom: 2rem !important;
            padding: 0 1rem !important;
          }
          
          /* Scale typography but keep it quiet and editorial */
          section:first-of-type h1 {
            font-size: clamp(1.5rem, 7vw, 2rem) !important;
            line-height: 1.2 !important;
          }
          
          section:first-of-type p {
            font-size: clamp(0.875rem, 3.5vw, 1rem) !important;
            line-height: 1.5 !important;
          }
          
          /* Button sizing */
          section:first-of-type button {
            padding: 0.75rem 1.5rem !important;
            font-size: 0.875rem !important;
          }
          
          /* Trust indicators - smaller but still readable */
          section:first-of-type > div:last-child > div:last-child {
            font-size: 0.6875rem !important;
            gap: 0.5rem !important;
          }
          
          /* ===== SECTIONS 2 & 4 - ADJUST TEXT POSITIONING ===== */
          section:nth-of-type(2) > div > div:first-child,
          section:nth-of-type(4) > div > div:last-child {
            padding: 48px 20px !important;
            margin-top: 45vh !important;
          }
          
          /* Reduce minimum heights slightly for small screens */
          section:nth-of-type(2) > div,
          section:nth-of-type(4) > div {
            min-height: 75vh !important;
          }
          
          /* Section 3 - tighter padding */
          section:nth-of-type(3) {
            padding: 48px 20px !important;
          }
          
          section:nth-of-type(3) > div {
            gap: 2.5rem !important;
          }
          
          /* Footer - smaller text */
          footer {
            padding: 1.5rem 1.5rem !important;
          }
          
          footer div {
            font-size: 0.8125rem !important;
          }
        }
        
        /* Prevent horizontal scroll on mobile */
        body {
          overflow-x: hidden;
        }
      `}</style>
    </div>
  );
};

export default LandingScandinavian;
